import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import TextareaAutosize from '@mui/base/TextareaAutosize';
import mainStyles from '../../pages/main.module.css';
import styles from './TagInput.module.css';
import TagFormat from '../../utils/tagFormat';
import { Tag } from '../../services/http/tag.api';
import DatePickerJp from '../DatePickerJp';
import Formatter from '../../utils/formatters';
import { ERROR_MESSAGES } from '../../utils/messages';
import useTrimmedPasteUndo from '../../hooks/useTrimmedPasteUndo.hook';

interface TagInputProps {
  tag: Tag;
  onChange: (value: string, isValidValue?: boolean) => void;
  onFocus?: () => void;
  className?: string;
  isDuplicated?: boolean;
}

function TagInput({
  tag, onChange, onFocus = () => ({}), className = '', isDuplicated = false,
}: TagInputProps) {
  const isValidValue = TagFormat.validate(tag.format, tag.formatCheck, tag.value);
  const trimmedPaste = useTrimmedPasteUndo({
    enabled: tag.format === TagFormat.NUMBER,
    value: tag.value,
    onValueChange: (value) => onChange(value, TagFormat.validate(tag.format, tag.formatCheck, value)),
  });

  const renderErrorMessage = () => {
    if (isDuplicated) {
      return (
        <div className={mainStyles['text-danger']} style={{ fontSize: 12 }}>
          {ERROR_MESSAGES.DUPLICATE_TAG_ERROR}
        </div>
      );
    }
    if (!isValidValue) {
      const invalidErrorMessage = TagFormat.getInvalidErrorMessage(tag.format, tag.formatCheck, tag.value);
      return (
        <div className={mainStyles['text-danger']} style={{ fontSize: 12 }}>
          {invalidErrorMessage}
        </div>
      );
    }
    return null;
  };

  if (tag.format === TagFormat.DATE) {
    return (
      <div className={className}>
        <DatePickerJp
          inputClassName={[className, isDuplicated ? styles.invalidBorder : ''].join(' ')}
          value={tag.value ? Formatter.fromFormatToDateTime(tag.value, 'yyyy/MM/dd') : null}
          onFocus={onFocus}
          onChange={() => {}}
          onInput={(value) => onChange(value)}
        />
        {renderErrorMessage()}
      </div>
    );
  }

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (trimmedPaste.recordInput((e.nativeEvent as Partial<InputEvent>).inputType)) return;
    const val = e.target.value;
    const isValidTag = TagFormat.validate(tag.format, tag.formatCheck, val);

    onChange(val, isValidTag);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (trimmedPaste.onPaste(e)) return;
    const val = e.currentTarget.value;
    const isValidTag = TagFormat.validate(tag.format, tag.formatCheck, val);
    onChange(val, isValidTag);
  };

  return (
    <div>
      <TextareaAutosize
        ref={trimmedPaste.targetRef}
        style={{ minHeight: '25px', paddingTop: '4px' }}
        className={[
          mainStyles.textareaAutosize,
          className,
          !isValidValue || isDuplicated ? styles.invalidBorder : '',
        ].join(' ')}
        value={tag.value}
        placeholder={TagFormat.getPlaceholder(tag.format, tag.formatCheck)}
        onChange={handleInput}
        onPaste={handlePaste}
        onKeyDown={trimmedPaste.onKeyDown}
        onBlur={handleInput}
        onFocus={onFocus}
      />
      {renderErrorMessage()}
    </div>
  );
}

export default TagInput;
